import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TNil } from '@app/app.interface';
import { CampaignFormPayloadService } from '@campaign-form/campaign-form-payload.service';
import { ICampaignCreatorForm, ICtaForm } from '@campaign-form/campaign-form.interface';
import { CampaignDatesService } from '@campaign-form/form-fields/campaign-basics/campaign-dates/campaign-dates.service';
import {
  ICtaTemplateApi,
  TCtaTemplateCreatePayload,
} from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import { TExecutionApi } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { CustomValuesService } from '@core/services/custom-values.service';
import { TranslateService } from '@ngx-translate/core';
import { TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { addMinutes, differenceInMinutes, isAfter, isBefore, startOfDay } from 'date-fns';
import { DATE_TIME_FORMAT, DateUtilitiesService } from './date-utilities.service';

export interface ICtaDuration {
  creatorId?: string;
  creatorName?: string;
  ctaName: string;
  duration: number;
}

@Injectable({
  providedIn: 'root',
})
export class CampaignDurationUtilitiesService {
  public longestCtaGlobalGamePlayDuration: number | null = null;
  public longestCtaCustomGamePlayDuration: number | null = null;
  public longestCtaCustomGamePlayDurationCreatorName: string | TNil = null;

  public shortestCtaGlobalExecutionDuration: number | null = null;
  public shortestCtaCustomExecutionDuration: number | null = null;
  public shortestCtaCustomExecutionDurationCreatorName: string | TNil = null;

  public longestCampaignGlobalGamePlayDuration: number | null = null;
  public longestCampaignGlobalGamePlayDurationCtaName: string | null = null;
  public longestCampaignCustomGamePlayDuration: number | null = null;
  public longestCampaignCustomGamePlayDurationCtaName: string | null = null;
  public longestCampaignCustomGamePlayDurationCreatorName: string | TNil = null;

  public earliestCampaignGlobalExecutionDate: Date | null = null;
  public earliestCampaignGlobalExecutionDateCtaName: string | null = null;
  public earliestCampaignCustomExecutionDate: Date | null = null;
  public earliestCampaignCustomExecutionDateCtaName: string | null = null;
  public earliestCampaignCustomExecutionDateCreatorName: string | TNil = null;
  public latestCampaignGlobalExecutionDate: Date | null = null;
  public latestCampaignGlobalExecutionDateCtaName: string | null = null;
  public latestCampaignCustomExecutionDate: Date | null = null;
  public latestCampaignCustomExecutionDateCtaName: string | null = null;
  public latestCampaignCustomExecutionDateCreatorName: string | TNil = null;

  public latestCampaignGlobalExecutionEnd: Date | null = null;
  public latestCampaignGlobalExecutionEndCtaName: string | null = null;
  public latestCampaignCustomExecutionEnd: Date | null = null;
  public latestCampaignCustomExecutionEndCtaName: string | null = null;
  public latestCampaignCustomExecutionEndCreatorName: string | TNil = null;

  public longestCampaignGlobalExecutionDuration: number | null = null;
  public longestCampaignGlobalExecutionDurationCtaName: string | null = null;
  public longestCampaignCustomExecutionDuration: number | null = null;
  public longestCampaignCustomExecutionDurationCtaName: string | null = null;
  public longestCampaignCustomExecutionDurationCreatorName: string | TNil = null;

  public campaignDuration: number | null = null;

  private readonly customValuesService = inject(CustomValuesService);
  private readonly campaignDatesService = inject(CampaignDatesService);
  private readonly campaignFormPayloadService = inject(CampaignFormPayloadService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly translate = inject(TranslateService);

  private campaignStartDate: Date | null = null;
  private campaignEndDate: Date | null = null;

  constructor() {
    this.campaignDatesService.startDate$.subscribe((startDate) => {
      this.campaignStartDate = startDate;
      this.calculateCampaignDuration();
    });
    this.campaignDatesService.endDate$.subscribe((endDate) => {
      this.campaignEndDate = endDate;
      this.calculateCampaignDuration();
    });
  }

  public calculateCampaignDurations(
    campaign: TCampaignApi,
    campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][],
    ctasValue: FormGroup<ICtaForm>['value'][],
    mappedCtaTemplates?: TCtaTemplateCreatePayload[],
  ): void {
    const templates =
      mappedCtaTemplates ??
      this.campaignFormPayloadService.getMappedCtaTemplates(campaign, campaignCreatorsValue, ctasValue);

    templates.forEach((ctaTemplate) => {
      this.calculateCampaignGlobalDurations(ctaTemplate);
      this.calculateCampaignCustomDurations(ctaTemplate);
    });
  }

  public calculateCtaDurations(
    campaign: TCampaignApi,
    campaignCreatorsValue: FormGroup<ICampaignCreatorForm>['value'][],
    ctasValue: FormGroup<ICtaForm>['value'][],
    ctaId: string,
    creatorId?: string,
  ): void {
    const clonedCampaignCreatorsValue = structuredClone(campaignCreatorsValue);

    const filteredCampaignCreatorsValue = clonedCampaignCreatorsValue.reduce(
      (acc, campaignCreator) => {
        campaignCreator.ctas = ctasValue.filter((cta) => cta.id === ctaId);

        if (campaignCreator.ctas.length) {
          acc.push(campaignCreator);
        }

        return acc;
      },
      [] as FormGroup<ICampaignCreatorForm>['value'][],
    );

    const filteredCtasValue = ctasValue.filter((cta) => cta.id === ctaId);

    const mappedCtaTemplates = this.campaignFormPayloadService.getMappedCtaTemplates(
      campaign,
      filteredCampaignCreatorsValue,
      filteredCtasValue,
    );

    mappedCtaTemplates.forEach((ctaTemplate) => {
      ctaTemplate.gamePlays?.forEach((gamePlay) => {
        if (gamePlay.duration > (this.longestCtaGlobalGamePlayDuration ?? 0)) {
          this.longestCtaGlobalGamePlayDuration = gamePlay.duration;
        }
      });

      ctaTemplate.executions?.forEach((execution) => {
        if (execution.duration && execution.duration < (this.shortestCtaGlobalExecutionDuration ?? 0)) {
          this.shortestCtaGlobalExecutionDuration = execution.duration;
        }
      });

      ctaTemplate.ctaCreators?.forEach((ctaCreator) => {
        if (creatorId && ctaCreator.creatorId !== creatorId) {
          return;
        }

        const ctaWithCustomValues = this.customValuesService.getCtaWithCreatorCustomValues(
          ctaTemplate as ICtaTemplateApi,
          ctaCreator.creatorId,
        );

        ctaWithCustomValues.gamePlays?.forEach((gamePlay) => {
          if (gamePlay.duration > (this.longestCtaCustomGamePlayDuration ?? 0)) {
            this.longestCtaCustomGamePlayDuration = gamePlay.duration;
            this.longestCtaCustomGamePlayDurationCreatorName = ctaCreator.creatorName ?? '';
          }
        });

        ctaWithCustomValues.executions?.forEach((execution) => {
          if (execution.duration && execution.duration < (this.shortestCtaCustomExecutionDuration ?? 0)) {
            this.shortestCtaCustomExecutionDuration = execution.duration;
            this.shortestCtaCustomExecutionDurationCreatorName = ctaCreator.creatorName ?? '';
          }
        });
      });
    });
  }

  public reset(): void {
    this.longestCtaGlobalGamePlayDuration = null;
    this.longestCtaCustomGamePlayDuration = null;
    this.longestCtaCustomGamePlayDurationCreatorName = null;
    this.shortestCtaGlobalExecutionDuration = null;
    this.shortestCtaCustomExecutionDuration = null;
    this.shortestCtaCustomExecutionDurationCreatorName = null;
    this.longestCampaignGlobalGamePlayDuration = null;
    this.longestCampaignGlobalGamePlayDurationCtaName = null;
    this.longestCampaignCustomGamePlayDuration = null;
    this.longestCampaignCustomGamePlayDurationCtaName = null;
    this.longestCampaignCustomGamePlayDurationCreatorName = null;
    this.earliestCampaignGlobalExecutionDate = null;
    this.earliestCampaignGlobalExecutionDateCtaName = null;
    this.earliestCampaignCustomExecutionDate = null;
    this.earliestCampaignCustomExecutionDateCtaName = null;
    this.earliestCampaignCustomExecutionDateCreatorName = null;
    this.latestCampaignGlobalExecutionDate = null;
    this.latestCampaignGlobalExecutionDateCtaName = null;
    this.latestCampaignCustomExecutionDate = null;
    this.latestCampaignCustomExecutionDateCtaName = null;
    this.latestCampaignCustomExecutionDateCreatorName = null;
    this.latestCampaignGlobalExecutionEnd = null;
    this.latestCampaignGlobalExecutionEndCtaName = null;
    this.latestCampaignCustomExecutionEnd = null;
    this.latestCampaignCustomExecutionEndCtaName = null;
    this.latestCampaignCustomExecutionEndCreatorName = null;
    this.longestCampaignGlobalExecutionDuration = null;
    this.longestCampaignGlobalExecutionDurationCtaName = null;
    this.longestCampaignCustomExecutionDuration = null;
    this.longestCampaignCustomExecutionDurationCtaName = null;
    this.longestCampaignCustomExecutionDurationCreatorName = null;
    this.campaignDuration = null;
    this.campaignStartDate = null;
    this.campaignEndDate = null;
  }

  // eslint-disable-next-line max-lines-per-function
  public getCampaignDatesError(): string | null {
    return this.getCampaignDatesErrorExecution() ?? this.getCampaignDatesErrorGamePlay();
  }

  private getCampaignDatesErrorExecution(): string | null {
    const error = this.getCampaignDatesErrorExecutionForStartDate() ?? this.getCampaignDatesErrorExecutionForEndDate();

    if (error) {
      return error;
    }

    if (
      this.longestCampaignGlobalExecutionDuration &&
      this.campaignDuration &&
      this.longestCampaignGlobalExecutionDuration > this.campaignDuration
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.longestCampaignGlobalExecutionDurationCtaName,
      })}`;
    }

    if (
      this.longestCampaignCustomExecutionDuration &&
      this.campaignDuration &&
      this.longestCampaignCustomExecutionDuration > this.campaignDuration
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.longestCampaignCustomExecutionDurationCtaName,
      })}`;
    }

    return null;
  }

  private getCampaignDatesErrorGamePlay(): string | null {
    if (
      this.longestCampaignGlobalGamePlayDuration &&
      this.campaignDuration &&
      this.longestCampaignGlobalGamePlayDuration > this.campaignDuration
    ) {
      return `${this.translate.instant('shared.form.error.gamePlaysDoNotFitInCampaignRange', {
        ctaName: this.longestCampaignGlobalGamePlayDurationCtaName,
      })} (${this.longestCampaignGlobalGamePlayDuration} ${this.translate.instant('shared.timeSuffix.minute')})`;
    }

    if (
      this.longestCampaignCustomGamePlayDuration &&
      this.campaignDuration &&
      this.longestCampaignCustomGamePlayDuration > this.campaignDuration
    ) {
      return `${this.translate.instant('shared.form.error.gamePlaysDoNotFitInCampaignRange', {
        ctaName: this.longestCampaignCustomGamePlayDurationCtaName,
      })} (${this.longestCampaignCustomGamePlayDurationCreatorName} - ${this.translate.instant('shared.timeSuffix.minute')})`;
    }

    return null;
  }

  private getCampaignDatesErrorExecutionForStartDate(): string | null {
    if (
      this.earliestCampaignGlobalExecutionDate &&
      isAfter(this.campaignStartDate!, this.earliestCampaignGlobalExecutionDate)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.earliestCampaignGlobalExecutionDateCtaName,
      })} (${this.dateUtils.formatDate(this.earliestCampaignGlobalExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignGlobalExecutionDate &&
      isBefore(this.latestCampaignGlobalExecutionDate, this.campaignStartDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignGlobalExecutionDateCtaName,
      })} (${this.dateUtils.formatDate(this.latestCampaignGlobalExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.earliestCampaignCustomExecutionDate &&
      isAfter(this.campaignStartDate!, this.earliestCampaignCustomExecutionDate)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.earliestCampaignCustomExecutionDateCtaName,
      })} (${this.earliestCampaignCustomExecutionDateCreatorName} - ${this.dateUtils.formatDate(this.earliestCampaignCustomExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignCustomExecutionDate &&
      isBefore(this.latestCampaignCustomExecutionDate, this.campaignStartDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignCustomExecutionDateCtaName,
      })} (${this.latestCampaignCustomExecutionDateCreatorName} - ${this.dateUtils.formatDate(this.latestCampaignCustomExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignGlobalExecutionEnd &&
      isBefore(this.latestCampaignGlobalExecutionEnd, this.campaignStartDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignGlobalExecutionEndCtaName,
      })} (${this.dateUtils.formatDate(this.latestCampaignGlobalExecutionEnd, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignCustomExecutionEnd &&
      isBefore(this.latestCampaignCustomExecutionEnd, this.campaignStartDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignCustomExecutionEndCtaName,
      })} (${this.latestCampaignCustomExecutionEndCreatorName} - ${this.dateUtils.formatDate(this.latestCampaignCustomExecutionEnd, DATE_TIME_FORMAT)})`;
    }

    return null;
  }

  private getCampaignDatesErrorExecutionForEndDate(): string | null {
    if (
      this.earliestCampaignGlobalExecutionDate &&
      isBefore(this.campaignEndDate!, this.earliestCampaignGlobalExecutionDate)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.earliestCampaignGlobalExecutionDateCtaName,
      })} (${this.dateUtils.formatDate(this.earliestCampaignGlobalExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignGlobalExecutionDate &&
      isAfter(this.latestCampaignGlobalExecutionDate, this.campaignEndDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignGlobalExecutionDateCtaName,
      })} (${this.dateUtils.formatDate(this.latestCampaignGlobalExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.earliestCampaignCustomExecutionDate &&
      isBefore(this.campaignEndDate!, this.earliestCampaignCustomExecutionDate)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.earliestCampaignCustomExecutionDateCtaName,
      })} (${this.earliestCampaignCustomExecutionDateCreatorName} - ${this.dateUtils.formatDate(this.earliestCampaignCustomExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignCustomExecutionDate &&
      isAfter(this.latestCampaignCustomExecutionDate, this.campaignEndDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignCustomExecutionDateCtaName,
      })} (${this.latestCampaignCustomExecutionDateCreatorName} - ${this.dateUtils.formatDate(this.latestCampaignCustomExecutionDate, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignGlobalExecutionEnd &&
      isAfter(this.latestCampaignGlobalExecutionEnd, this.campaignEndDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignGlobalExecutionEndCtaName,
      })} (${this.dateUtils.formatDate(this.latestCampaignGlobalExecutionEnd, DATE_TIME_FORMAT)})`;
    }

    if (
      this.latestCampaignCustomExecutionEnd &&
      isAfter(this.latestCampaignCustomExecutionEnd, this.campaignEndDate!)
    ) {
      return `${this.translate.instant('shared.form.error.executionsDoNotFitInCampaignRange', {
        ctaName: this.latestCampaignCustomExecutionEndCtaName,
      })} (${this.latestCampaignCustomExecutionEndCreatorName} - ${this.dateUtils.formatDate(this.latestCampaignCustomExecutionEnd, DATE_TIME_FORMAT)})`;
    }

    return null;
  }

  private calculateCampaignCustomDurations(ctaTemplate: TCtaTemplateCreatePayload): void {
    ctaTemplate.ctaCreators?.forEach((ctaCreator) => {
      const ctaWithCustomValues = this.customValuesService.getCtaWithCreatorCustomValues(
        ctaTemplate as ICtaTemplateApi,
        ctaCreator.creatorId,
      );

      ctaWithCustomValues.gamePlays?.forEach((gamePlay) => {
        if (gamePlay.duration > (this.longestCampaignCustomGamePlayDuration ?? 0)) {
          this.longestCampaignCustomGamePlayDuration = gamePlay.duration;
          this.longestCampaignCustomGamePlayDurationCtaName = ctaWithCustomValues.name;
          this.longestCampaignCustomGamePlayDurationCreatorName = ctaCreator.creatorName;
        }
      });

      ctaWithCustomValues.executions?.forEach((execution) => {
        if (execution.duration && execution.duration > (this.longestCampaignCustomExecutionDuration ?? 0)) {
          this.longestCampaignCustomExecutionDuration = execution.duration;
          this.longestCampaignCustomExecutionDurationCtaName = ctaWithCustomValues.name;
          this.longestCampaignCustomExecutionDurationCreatorName = ctaCreator.creatorName;
        }

        const executionWindow = this.getExecutionWindow(execution);

        if (!executionWindow) {
          return;
        }

        const [executionTimestamp, executionEnd] = executionWindow;

        if (!this.latestCampaignCustomExecutionEnd) {
          this.latestCampaignCustomExecutionEnd = executionTimestamp;
          this.latestCampaignCustomExecutionEndCtaName = ctaWithCustomValues.name;
          this.latestCampaignCustomExecutionEndCreatorName = ctaCreator.creatorName;
        }

        if (
          !this.latestCampaignCustomExecutionDate ||
          isAfter(executionTimestamp, this.latestCampaignCustomExecutionDate)
        ) {
          this.latestCampaignCustomExecutionDate = executionTimestamp;
          this.latestCampaignCustomExecutionDateCtaName = ctaWithCustomValues.name;
          this.latestCampaignCustomExecutionDateCreatorName = ctaCreator.creatorName;
        }

        if (
          !this.earliestCampaignCustomExecutionDate ||
          isBefore(executionTimestamp, this.earliestCampaignCustomExecutionDate)
        ) {
          this.earliestCampaignCustomExecutionDate = executionTimestamp;
          this.earliestCampaignCustomExecutionDateCtaName = ctaWithCustomValues.name;
          this.earliestCampaignCustomExecutionDateCreatorName = ctaCreator.creatorName;
        }

        if (isAfter(executionEnd, this.latestCampaignCustomExecutionEnd)) {
          this.latestCampaignCustomExecutionEnd = executionEnd;
          this.latestCampaignCustomExecutionEndCtaName = ctaWithCustomValues.name;
          this.latestCampaignCustomExecutionEndCreatorName = ctaCreator.creatorName;
        }
      });
    });
  }

  private calculateCampaignGlobalDurations(ctaTemplate: TCtaTemplateCreatePayload): void {
    ctaTemplate.gamePlays?.forEach((gamePlay) => {
      if (gamePlay.duration > (this.longestCampaignGlobalGamePlayDuration ?? 0)) {
        this.longestCampaignGlobalGamePlayDuration = gamePlay.duration;
        this.longestCampaignGlobalGamePlayDurationCtaName = ctaTemplate.name;
      }
    });

    ctaTemplate.executions?.forEach((execution) => {
      if (execution.duration && execution.duration > (this.longestCampaignGlobalExecutionDuration ?? 0)) {
        this.longestCampaignGlobalExecutionDuration = execution.duration;
        this.longestCampaignGlobalExecutionDurationCtaName = ctaTemplate.name;
      }

      const executionWindow = this.getExecutionWindow(execution);

      if (!executionWindow) {
        return;
      }

      const [executionTimestamp, executionEnd] = executionWindow;

      if (!this.latestCampaignGlobalExecutionEnd) {
        this.latestCampaignGlobalExecutionEnd = executionTimestamp;
        this.latestCampaignGlobalExecutionEndCtaName = ctaTemplate.name;
      }

      if (
        !this.latestCampaignGlobalExecutionDate ||
        isAfter(executionTimestamp, this.latestCampaignGlobalExecutionDate)
      ) {
        this.latestCampaignGlobalExecutionDate = executionTimestamp;
        this.latestCampaignGlobalExecutionDateCtaName = ctaTemplate.name;
      }

      if (
        !this.earliestCampaignGlobalExecutionDate ||
        isBefore(executionTimestamp, this.earliestCampaignGlobalExecutionDate)
      ) {
        this.earliestCampaignGlobalExecutionDate = executionTimestamp;
        this.earliestCampaignGlobalExecutionDateCtaName = ctaTemplate.name;
      }

      if (isAfter(executionEnd, this.latestCampaignGlobalExecutionEnd)) {
        this.latestCampaignGlobalExecutionEnd = executionEnd;
        this.latestCampaignGlobalExecutionEndCtaName = ctaTemplate.name;
      }
    });
  }

  // [start, end] an execution occupies inside the campaign: a unique execution's moment plus its
  // duration, or a time_frame's window as entered. Other types are not anchored in time.
  private getExecutionWindow(execution: TExecutionApi): [Date, Date] | null {
    if (execution.executionStartDate && execution.executionEndDate) {
      return [
        this.dateUtils.parseDateTimeFromApi(execution.executionStartDate),
        this.dateUtils.parseDateTimeFromApi(execution.executionEndDate),
      ];
    }

    if (!execution.executionDate) {
      return null;
    }

    const executionDate = this.dateUtils.parseDateTimeFromApi(execution.executionDate);
    const executionTimestamp = this.dateUtils.combineDateTime(
      executionDate,
      execution.executionTime
        ? this.dateUtils.parseDateTimeFromApi(execution.executionTime)
        : startOfDay(executionDate),
    );

    return [executionTimestamp, addMinutes(executionTimestamp, execution.duration ?? 0)];
  }

  private calculateCampaignDuration(): void {
    if (this.campaignStartDate && this.campaignEndDate) {
      this.campaignDuration = differenceInMinutes(this.campaignEndDate, this.campaignStartDate);
    }
  }
}
